// Tarefas recorrentes nas Configurações (US-095): o toggle liga o recurso e abre a seção
// (AC-216); a lista tem editar e excluir em cada recorrente (AC-218) e o botão azul
// "Adicionar tarefa recorrente" abre o formulário com dias da semana e calendário (AC-217).
import { useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MONTH_INTERVALS,
  resolveMonthStart,
  validateRecurringInput,
  WEEKDAY_ORDER,
  type MonthInterval,
  type RecurringError,
  type RecurringField,
  type RecurringTask,
} from '../domain/recurring';
import { formatTime, toDateKey } from '../domain/dates';
import { formatMinutes } from '../i18n/format';
import { useAppState, useStore } from '../state/AppStore';
import { ConfirmDialog, Dialog } from './EditTaskDialog';
import { SectionTrash } from './SectionTrash';
import { Icon } from './Icon';
import type { TaskError, TaskField } from '../domain/task';
import { cls, emptyFields, TaskDetailsEditor, type FieldsState } from './NewTaskForm';
import { TimeInput } from './TimeInput';

interface Draft {
  fields: FieldsState;
  weekdays: number[];
  monthDays: number[];
  /** dias do mês: todos os meses, bimestral, trimestral ou semestral (AC-224) */
  monthInterval: MonthInterval;
}

type Errors = Partial<Record<RecurringField, RecurringError>>;

const emptyDraft = (): Draft => ({ fields: emptyFields(), weekdays: [], monthDays: [], monthInterval: 1 });

const draftOf = (r: RecurringTask): Draft => ({
  fields: { title: r.title, description: r.description ?? '', date: null, time: r.due_time ?? '', estimate: r.estimate_minutes },
  weekdays: r.weekdays,
  monthDays: r.month_days,
  monthInterval: r.month_interval ?? 1,
});

/** Campos para salvar; o início da contagem dos meses vem de resolveMonthStart (ASM-119 / ASM-120). */
const toInput = (d: Draft, prev: RecurringTask | null, today: string) => ({
  title: d.fields.title,
  description: d.fields.description,
  due_time: d.fields.time || null,
  estimate_minutes: d.fields.estimate,
  weekdays: d.weekdays,
  month_days: d.monthDays,
  month_interval: d.monthInterval,
  month_start: resolveMonthStart({ month_days: d.monthDays, month_interval: d.monthInterval }, prev, today),
});

/** "Seg", "Ter"… no idioma do app (sem o ponto da abreviação). */
function weekdayName(day: number, language: string): string {
  // 1º/2/2026 foi domingo: 1 + day cai no dia da semana pedido
  const short = new Intl.DateTimeFormat(language, { weekday: 'short' }).format(new Date(2026, 1, 1 + day)).replace(/\.$/, '');
  return short.charAt(0).toLocaleUpperCase(language) + short.slice(1);
}

const toggle = (list: number[], n: number) => (list.includes(n) ? list.filter((x) => x !== n) : [...list, n]);

/** Quando a recorrente acontece: Seg…Dom, "Calendário" (dias do mês) e horário (ASM-110 / ASM-112). */
function DaysPicker({ draft, onChange, errors, disabled }: { draft: Draft; onChange(d: Draft): void; errors: Errors; disabled?: boolean }) {
  const { t, i18n } = useTranslation();
  const [calendarOpen, setCalendarOpen] = useState(draft.monthDays.length > 0);
  const timeFormat = useAppState((s) => s.profile?.time_format ?? '24h');
  const monthDays = [...draft.monthDays].sort((a, b) => a - b);
  const everyDay = WEEKDAY_ORDER.every((d) => draft.weekdays.includes(d));
  return (
    <div className="recurring-days">
      <p className="label-caps">{t('recurring.days')}</p>
      <div className="chips">
        {WEEKDAY_ORDER.map((day) => (
          <button
            key={day}
            type="button"
            className={cls('chip', draft.weekdays.includes(day) && 'chip--on')}
            aria-pressed={draft.weekdays.includes(day)}
            disabled={disabled}
            onClick={() => onChange({ ...draft, weekdays: toggle(draft.weekdays, day) })}
          >
            {weekdayName(day, i18n.language)}
          </button>
        ))}
        {/* "Todos os dias" marca ou desmarca Seg a Dom de uma vez (AC-228) */}
        <button
          type="button"
          className={cls('chip', everyDay && 'chip--on')}
          aria-pressed={everyDay}
          disabled={disabled}
          onClick={() => onChange({ ...draft, weekdays: everyDay ? [] : [...WEEKDAY_ORDER] })}
        >
          {t('recurring.everyDay')}
        </button>
        <button
          type="button"
          className={cls('chip', monthDays.length > 0 && 'chip--on')}
          aria-expanded={calendarOpen}
          disabled={disabled}
          onClick={() => setCalendarOpen(!calendarOpen)}
        >
          <Icon name="calendar" size={13} />
          {monthDays.length ? `${t('recurring.calendar')} · ${monthDays.join(', ')}` : t('recurring.calendar')}
        </button>
      </div>
      {calendarOpen && (
        <div className="month-days" role="group" aria-label={t('recurring.calendar')}>
          <p className="month-days__hint">{t('recurring.calendarHint')}</p>
          {/* opções rápidas de período; a grade de dias continua igual (AC-224) */}
          <div className="chips month-days__period" role="group" aria-label={t('recurring.periodLabel')}>
            {MONTH_INTERVALS.map((n) => (
              <button
                key={n}
                type="button"
                className={cls('chip', draft.monthInterval === n && 'chip--on')}
                aria-pressed={draft.monthInterval === n}
                disabled={disabled}
                onClick={() => onChange({ ...draft, monthInterval: n })}
              >
                {t(`recurring.period.${n}`)}
              </button>
            ))}
          </div>
          <div className="month-days__grid">
            {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
              <button
                key={day}
                type="button"
                className={cls('calendar__day', draft.monthDays.includes(day) && 'is-on')}
                aria-label={t('recurring.monthDay', { day })}
                aria-pressed={draft.monthDays.includes(day)}
                disabled={disabled}
                onClick={() => onChange({ ...draft, monthDays: toggle(draft.monthDays, day) })}
              >
                {day}
              </button>
            ))}
          </div>
        </div>
      )}
      {errors.days && <p className="field-error">{t(`errors.task.${errors.days}`)}</p>}
      <div className="chips recurring-days__time">
        <TimeInput
          className={cls('chip', draft.fields.time && 'chip--on')}
          label={t('recurring.time')}
          value={draft.fields.time}
          format={timeFormat}
          disabled={disabled}
          onChange={(time) => onChange({ ...draft, fields: { ...draft.fields, time } })}
        />
      </div>
      {errors.due_time && <p className="field-error">{t(`errors.task.${errors.due_time}`)}</p>}
    </div>
  );
}

/** Título + quando + descrição e duração (as mesmas da tarefa). */
function RecurringEditor({ draft, onChange, errors, disabled, titleLabel, autoFocus }: { draft: Draft; onChange(d: Draft): void; errors: Errors; disabled?: boolean; titleLabel: string; autoFocus?: boolean }) {
  const { t } = useTranslation();
  return (
    <>
      <div className="new-task__row">
        <Icon name="repeat" size={16} className="recurring-form__icon" />
        <input
          className="new-task__input"
          aria-label={titleLabel}
          placeholder={t('recurring.titlePlaceholder')}
          value={draft.fields.title}
          disabled={disabled}
          autoFocus={autoFocus}
          onChange={(e) => onChange({ ...draft, fields: { ...draft.fields, title: e.target.value } })}
        />
      </div>
      {errors.title && <p className="field-error">{t(`errors.task.${errors.title}`, { max: 120 })}</p>}
      <TaskDetailsEditor
        value={draft.fields}
        onChange={(fields) => onChange({ ...draft, fields })}
        // os erros de horário e de dias aparecem no DaysPicker; aqui, descrição e duração
        errors={{ description: errors.description, estimate_minutes: errors.estimate_minutes } as Partial<Record<TaskField, TaskError>>}
        disabled={disabled}
        schedule={<DaysPicker draft={draft} onChange={onChange} errors={errors} disabled={disabled} />}
      />
    </>
  );
}

/** Valida e devolve os campos, ou mostra os erros. */
function useSave(draft: Draft, setErrors: (e: Errors) => void, prev: RecurringTask | null) {
  const store = useStore();
  return () => {
    const v = validateRecurringInput(toInput(draft, prev, toDateKey(new Date(store.now()))));
    if (!v.ok) {
      // título vazio não salva, sem mensagem (como na tarefa — AC-002)
      setErrors(draft.fields.title.trim() ? v.errors : { ...v.errors, title: undefined });
      return null;
    }
    setErrors({});
    return v.value;
  };
}

function AddRecurring({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const store = useStore();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [errors, setErrors] = useState<Errors>({});
  const validate = useSave(draft, setErrors, null);
  const close = () => {
    setOpen(false);
    setDraft(emptyDraft());
    setErrors({});
  };
  const submit = async () => {
    const value = validate();
    if (!value) return;
    close();
    await store.createRecurring(value);
  };
  // Enter salva (menos na descrição); Esc fecha — como no "Adicionar tarefa"
  const onKey = (e: KeyboardEvent<HTMLFormElement>) => {
    const el = e.target as HTMLElement;
    if (e.key === 'Escape') return close();
    if (e.key === 'Enter' && el.tagName === 'INPUT') {
      e.preventDefault();
      void submit();
    }
  };

  if (!open) {
    return (
      <button type="button" className="btn btn--add btn--add-recurring btn--block" disabled={!canEdit} onClick={() => setOpen(true)}>
        <Icon name="plus" size={16} />
        {t('recurring.add')}
      </button>
    );
  }
  return (
    <form
      className="new-task new-task--open recurring-form"
      data-testid="recurring-form"
      onKeyDown={onKey}
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <RecurringEditor draft={draft} onChange={setDraft} errors={errors} disabled={!canEdit} titleLabel={t('recurring.titleField')} autoFocus />
      <div className="new-task__footer">
        <button type="button" className="btn btn--ghost" onClick={close}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="btn btn--add btn--add-recurring" disabled={!canEdit}>
          <Icon name="check" size={14} />
          {t('common.save')}
        </button>
      </div>
    </form>
  );
}

function EditRecurringDialog({ rec, onClose }: { rec: RecurringTask; onClose(): void }) {
  const { t } = useTranslation();
  const store = useStore();
  const [draft, setDraft] = useState<Draft>(() => draftOf(rec));
  const [errors, setErrors] = useState<Errors>({});
  const validate = useSave(draft, setErrors, rec);
  const save = async () => {
    const value = validate();
    if (!value) return;
    onClose();
    await store.updateRecurring(rec.id, value);
  };
  return (
    <Dialog labelId="edit-recurring-title" onClose={onClose}>
      <div className="dialog__head">
        <h2 id="edit-recurring-title" className="dialog__title">
          {t('recurring.editTitle')}
        </h2>
        <button type="button" className="icon-btn" aria-label={t('common.close')} onClick={onClose}>
          <Icon name="x" size={16} />
        </button>
      </div>
      <form
        className="recurring-form"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <RecurringEditor draft={draft} onChange={setDraft} errors={errors} titleLabel={t('task.titleField')} />
        <div className="dialog__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn btn--add btn--add-recurring">
            <Icon name="check" size={14} />
            {t('common.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** "Seg, Qua · Dias 5, 20" */
function daysSummary(rec: RecurringTask, language: string, t: (k: string, o?: Record<string, unknown>) => string): string {
  const parts: string[] = [];
  const week = WEEKDAY_ORDER.filter((d) => rec.weekdays.includes(d)).map((d) => weekdayName(d, language));
  if (week.length === WEEKDAY_ORDER.length) parts.push(t('recurring.everyDay'));
  else if (week.length) parts.push(week.join(', '));
  if (rec.month_days.length) {
    const days = [...rec.month_days].sort((a, b) => a - b);
    const monthly = t('recurring.monthDays', { count: days.length, days: days.join(', ') });
    parts.push(rec.month_interval > 1 ? `${monthly} · ${t(`recurring.periodShort.${rec.month_interval}`)}` : monthly);
  }
  return parts.join(' · ');
}

function RecurringRow({ rec, canEdit }: { rec: RecurringTask; canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const timeFormat = useAppState((s) => s.profile?.time_format ?? '24h');
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  return (
    <li className="task task--recurring" data-testid="recurring-row">
      <div className="task__top">
        <Icon name="repeat" size={14} className="task__repeat" />
        <span className="task__title" title={rec.title}>
          {rec.title}
        </span>
        <div className="task__side">
          <div className="task__actions">
            <button type="button" className="icon-btn icon-btn--sm" aria-label={t('task.menu')} aria-expanded={menu} onClick={() => setMenu(!menu)}>
              <Icon name="more" size={16} />
            </button>
          </div>
          {menu && (
            <div className="menu" role="menu">
              <button
                type="button"
                role="menuitem"
                disabled={!canEdit}
                onClick={() => {
                  setMenu(false);
                  setEditing(true);
                }}
              >
                <Icon name="edit" size={14} />
                {t('task.edit')}
              </button>
              <button
                type="button"
                role="menuitem"
                className="menu__danger"
                disabled={!canEdit}
                onClick={() => {
                  setMenu(false);
                  setConfirmDelete(true);
                }}
              >
                <Icon name="trash" size={14} />
                {t('task.delete')}
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="task__meta">
        <span>
          <Icon name="calendar" size={13} />
          {daysSummary(rec, i18n.language, t)}
        </span>
        {rec.due_time && (
          <span>
            <Icon name="clock" size={13} />
            {formatTime(rec.due_time, timeFormat)}
          </span>
        )}
        <span>
          <Icon name="timer" size={13} />
          {rec.estimate_minutes ? formatMinutes(rec.estimate_minutes, t) : t('task.noEstimate')}
        </span>
      </div>
      {editing && <EditRecurringDialog rec={rec} onClose={() => setEditing(false)} />}
      {confirmDelete && (
        <ConfirmDialog
          title={t('recurring.deleteTitle')}
          body={t('recurring.deleteBody', { title: rec.title })}
          confirmLabel={t('task.delete')}
          danger
          onCancel={() => setConfirmDelete(false)}
          onConfirm={async () => {
            setConfirmDelete(false);
            await store.deleteRecurring(rec.id);
          }}
        />
      )}
    </li>
  );
}

export function RecurringSection({ online, canEdit }: { online: boolean; canEdit: boolean }) {
  const { t } = useTranslation();
  const store = useStore();
  const enabled = useAppState((s) => !!s.profile?.recurring_enabled);
  const list = useAppState((s) => s.recurring);
  return (
    <section className="settings__group" data-testid="recurring-section">
      <div className="section__bar">
        <h2 className="label-caps">{t('recurring.group')}</h2>
        {/* exclui todas as recorrentes, depois de perguntar (AC-236) */}
        {enabled && list.length > 0 && (
          <SectionTrash
            label={t('recurring.deleteAll')}
            title={t('recurring.deleteAllTitle', { count: list.length })}
            body={t('recurring.deleteAllBody')}
            disabled={!canEdit}
            onConfirm={() => store.deleteAllRecurring()}
          />
        )}
      </div>
      <div className="settings__card">
        <label className="settings__row">
          <span className="settings__label">
            <span>{t('recurring.toggle')}</span>
            <small>{t('recurring.toggleHint')}</small>
          </span>
          <input
            type="checkbox"
            role="switch"
            className="switch"
            checked={enabled}
            disabled={!online}
            aria-label={t('recurring.toggle')}
            onChange={(e) => void store.setRecurringEnabled(e.target.checked)}
          />
        </label>
      </div>
      {enabled && (
        <div className="recurring">
          {list.length ? (
            <ul className="recurring__list">
              {list.map((rec) => (
                <RecurringRow key={rec.id} rec={rec} canEdit={canEdit} />
              ))}
            </ul>
          ) : (
            <p className="recurring__empty">{t('recurring.empty')}</p>
          )}
          <AddRecurring canEdit={canEdit} />
        </div>
      )}
    </section>
  );
}
